/**
 * Performance monitoring for CheckCheck.be frontend
 * Reports Web Vitals (LCP, FCP, INP) and API call timing to backend.
 */

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

let metricsBuffer = [];
let flushTimer = null;

function bufferMetric(metric) {
  metricsBuffer.push(metric);
  // Flush every 10 seconds or when buffer hits 20 entries
  if (metricsBuffer.length >= 20) {
    flushMetrics();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flushMetrics, 10000);
  }
}

function flushMetrics() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (metricsBuffer.length === 0) return;

  const payload = {
    metrics: [...metricsBuffer],
    user_id: localStorage.getItem('ccc_user_id') || '',
  };
  metricsBuffer = [];

  // Use sendBeacon for reliability (fires even on page unload)
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      `${API_URL}/perf/report`,
      new Blob([JSON.stringify(payload)], { type: 'application/json' })
    );
  } else {
    fetch(`${API_URL}/perf/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  }
}

/**
 * Initialize Web Vitals reporting.
 * Call this once in App.js on mount.
 */
export function initPerformanceMonitoring() {
  import('web-vitals').then(({ onLCP, onFCP, onINP }) => {
    const page = window.location.pathname;

    onLCP((metric) => {
      bufferMetric({ type: 'LCP', value: metric.value, page });
    });

    onFCP((metric) => {
      bufferMetric({ type: 'FCP', value: metric.value, page });
    });

    onINP((metric) => {
      bufferMetric({ type: 'INP', value: metric.value, page });
    });
  }).catch(() => {});

  // Flush on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushMetrics();
    }
  });
}

/**
 * Report a custom page timing metric (e.g., component render time).
 */
export function reportPageTiming(page, durationMs) {
  bufferMetric({ type: 'page_load', value: durationMs, page });
}

/**
 * Report API call timing (called from axios interceptor).
 */
export function reportApiTiming(method, path, durationMs, status) {
  bufferMetric({
    type: 'api_call',
    value: durationMs,
    page: window.location.pathname,
    extra: { method, path, status },
  });
}

// ─── BUSINESS KPIs (§12): SEARCH TO RESULT / COMPANY TO REPORT ───
const flowMarks = {};

/** Mark the start of a business flow ('search' or 'report'). */
export function markFlow(name) {
  flowMarks[name] = performance.now();
}

/** Report elapsed time since markFlow(name) as a business KPI metric. */
export function reportFlow(name, type) {
  const start = flowMarks[name];
  if (!start) return;
  delete flowMarks[name];
  const elapsed = performance.now() - start;
  if (elapsed > 0 && elapsed < 120000) {
    bufferMetric({ type, value: elapsed, page: window.location.pathname });
  }
}
